/**
 * Scene 8 (dev): buss og bil kjører rutene sine samtidig, i free-roam.
 *
 * Ingen kamerastyring – brukeren panorerer/zoomer fritt mens kjøretøyene
 * kjører. Hvert kjøretøy følger sin rute (posisjon + retning fra sampleren),
 * og linja vokser bak det.
 *
 * FARTSPROFIL: hvert kjøretøy får en stykkevis konstant fart langs ruta
 * (`phases`: meter → m/s). Total reisetid følger direkte av profilen, så
 * bussen kan kjøre jevnt hele veien mens bilen kjører fort til Sandvika og
 * mye tregere videre inn mot sentrum. Gangstrekkene bruker samme gangfart.
 *
 * Klokka komprimeres med TIME_SCALE så ~52 min reell tur spilles på ~30 min.
 *
 * Studieverktøy, ikke web-produkt. Sluttproduktets race kan gjenbruke samme
 * driver med en kraftigere TIME_SCALE (f.eks. 0.01 for ~30 s).
 */

import { BUS_ROUTE, CAR_ROUTE } from '../data/route.js';
import { setTunnelVisibility } from './route.js';

// Standard scene-lengde (speedet opp) og reell total reisetid.
const DEFAULT_SCENE_MS = 30 * 1000;   // ~30 sek speedet opp
const REAL_TOTAL_MS = 56 * 60 * 1000; // bilens reelle reisetid (den lengste)
const FULL = { bus: BUS_ROUTE, car: CAR_ROUTE };

/**
 * Bygg en tid→distanse-oppslager fra en stykkevis konstant fartsprofil.
 * @param {Array<{untilDist:number, speed:number}>} phases
 * @param {number} routeLength
 * @returns {{ totalMs:number, distanceAt:(ms:number)=>number }}
 */
function buildTimeline(phases, routeLength) {
  // Sørg for at siste fase dekker hele ruta.
  const segs = [];
  let prevDist = 0;
  let cumMs = 0;
  for (const ph of phases) {
    const until = Math.min(ph.untilDist, routeLength);
    if (until <= prevDist) continue;
    const len = until - prevDist;
    const ms = (len / ph.speed) * 1000;
    segs.push({ startMs: cumMs, endMs: cumMs + ms, startDist: prevDist, endDist: until, speed: ph.speed });
    cumMs += ms;
    prevDist = until;
  }
  // Rest av ruta på siste fart, om profilen ikke nådde helt ut.
  if (prevDist < routeLength) {
    const speed = phases[phases.length - 1]?.speed ?? 10;
    const ms = ((routeLength - prevDist) / speed) * 1000;
    segs.push({ startMs: cumMs, endMs: cumMs + ms, startDist: prevDist, endDist: routeLength, speed });
    cumMs += ms;
  }
  return {
    totalMs: cumMs,
    distanceAt(ms) {
      if (ms <= 0) return 0;
      if (ms >= cumMs) return routeLength;
      for (const s of segs) {
        if (ms <= s.endMs) {
          const t = (ms - s.startMs) / (s.endMs - s.startMs);
          return s.startDist + (s.endDist - s.startDist) * t;
        }
      }
      return routeLength;
    },
  };
}

/**
 * @param {object} opts
 * @param {import('maplibre-gl').Map} opts.map
 * @param {number} opts.exaggeration      terreng-eksagerering
 * @param {Array} opts.vehicles           [{ mode, sampler, vehicle, marker, phases, startDelaySec }]
 * @param {(realMs:number)=>void} [opts.onTime]  simulert (reell) tid pr. frame
 */
export function createScene8({ map, exaggeration = 1, vehicles, onTime }) {
  let raf = null;
  let started = 0;
  let timeScale = DEFAULT_SCENE_MS / REAL_TOTAL_MS;

  for (const v of vehicles) {
    v.timeline = buildTimeline(v.phases, v.sampler.length);
  }

  const drawTo = (v, d) => {
    map.getSource(`route-${v.mode}`)?.setData(
      d >= v.sampler.length ? FULL[v.mode] : v.sampler.sliceTo(d),
    );
  };

  const place = (v, d) => {
    const p = v.vehicle.setDistance(d);
    // Tunnel: kjøretøymodellen forsvinner (setDistance skjuler den) og den
    //   fargede tracken skjules (tunnel-lag av i play()), MEN markøren blir
    //   værende 100 m over – så man ser hvor kjøretøyet er.
    // Walk (gangstrekk): både kjøretøy OG markør forsvinner; bare den
    //   stiplede gang-linja vises videre fram til målet.
    const walking = p.structure === 'walk';
    if (walking) v.vehicle.setHidden(true);
    v.marker.setLngLat(p.lngLat, (p.elevation ?? 0) * exaggeration);
    v.marker.setHidden(walking);
    drawTo(v, d);
  };

  function frame(now) {
    let anyRunning = false;
    for (const v of vehicles) {
      const startDelayMs = (v.startDelaySec ?? 0) * 1000 * timeScale;
      const realMs = (now - started - startDelayMs) / timeScale;
      place(v, v.timeline.distanceAt(realMs));
      if (realMs < v.timeline.totalMs) anyRunning = true;
    }
    // Simulert klokke = scene-tid delt på skala (uten start-forsinkelser).
    onTime?.((now - started) / timeScale);
    raf = anyRunning ? requestAnimationFrame(frame) : null;
  }

  /** Nullstill til startlinja: begge kjøretøy + markør ved rutestart. */
  const reset = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = null;
    setTunnelVisibility(map, false);
    for (const v of vehicles) {
      v.vehicle.setHidden(false);
      v.marker.setVisible(true);
      place(v, 0);
    }
    onTime?.(0);
  };

  return {
    label: 'Scene 8 (buss + bil)',

    /**
     * Spill av racet.
     * @param {object} [opts]
     * @param {number} [opts.sceneMs]  scene-lengde i ms (kort=speedet opp,
     *   REAL_TOTAL_MS=sanntid). Default DEFAULT_SCENE_MS.
     */
    play(opts = {}) {
      // Kanseller en evt. pågående loop først, ellers kan flere frame-løkker
      // kjøre samtidig og overskrive rute-kilden (rutene «blinker ut»).
      if (raf) cancelAnimationFrame(raf);
      raf = null;
      timeScale = (opts.sceneMs ?? DEFAULT_SCENE_MS) / REAL_TOTAL_MS;
      // Skjul de stiplede tunnel-trackene under racet: i tunnel ser man bare
      // markøren svevende, ikke sporet på bakken over tunnelen.
      setTunnelVisibility(map, false);
      for (const v of vehicles) {
        v.vehicle.setHidden(false);
        v.marker.setVisible(true);
        place(v, 0);
      }
      started = performance.now();
      raf = requestAnimationFrame(frame);
    },

    /** Sanntid: samme race, men i faktisk reisetid (~52 min). */
    playRealtime() {
      this.play({ sceneMs: REAL_TOTAL_MS });
    },

    /**
     * Vis rutene FERDIG opptegnet, uten animasjon: begge ruter fullt tegnet
     * (inkl. tunnel-segmentene, stiplet), kjøretøy og markører skjult. Et rent
     * oversiktsbilde av korridoren.
     */
    showFinished() {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
      // Her VIL vi se hele ruta, også der den går i tunnel (stiplet).
      setTunnelVisibility(map, true);
      for (const v of vehicles) {
        map.getSource(`route-${v.mode}`)?.setData(FULL[v.mode]);
        v.vehicle.setHidden(true);
        v.marker.setVisible(false);
      }
      onTime?.(0);
    },

    reset,

    stop() {
      // Frys der de er (pause). Neste play() starter forfra.
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    },
  };
}
